import * as api from "../api";
import { v4 as uuidv4 } from "uuid";
import { Circle, Rectangle, Polygon } from "leaflet";

const login = async ({ commit }, data) => {
  return api.login(data.email, data.password).then(async (response) => {
    if (response.ok) {
      const token = await response.json();
      commit("SET_TOKEN_DATA", token);
    } else {
      const error = await response.text();
      return Promise.reject(error);
    }
  });
};

const logout = async ({ commit, state }) => {
  return api.logout(state.token).then(() => {
    commit("LOGOUT");
  });
};

function createTriangle(lat, lon, options) {
  const degrees = options.radius / 111111.0;
  const latScale = Math.cos(lat * (Math.PI / 180.0));

  const angle = Math.PI / 6.0; // 30 degrees
  const p1 = [lat + degrees, lon];
  const p2 = [
    lat - degrees * Math.sin(angle),
    lon - (Math.cos(angle) * degrees) / latScale,
  ];
  const p3 = [p2[0], lon + (Math.cos(angle) * degrees) / latScale];

  return new Polygon([p1, p2, p3], options);
}

function createSquare(lat, lon, options) {
  const degrees = options.radius / 111111.0;
  const latScale = Math.cos(lat * (Math.PI / 180.0));

  return new Rectangle(
    [
      [lat - degrees, lon - degrees / latScale],
      [lat + degrees, lon + degrees / latScale],
    ],
    options
  );
}

function addUnitMarker(state, unit_mac, unit) {
  let marker_id = null;

  if (unit.latitude && unit.longitude) {
    let marker;
    const options = {
      color: "black",
      fillOpacity: 0.33,
      fillColor: "black",
      radius: null,
      // don't allow a mouse event to propagate to the underlying map
      bubblingMouseEvents: false,
    };
    if (unit.purpose === "sprinkler") {
      options.radius = unit.radius;
      marker = new Circle([unit.latitude, unit.longitude], options);
    } else if (unit.purpose === "base_station") {
      options.radius = 5;
      marker = createSquare(unit.latitude, unit.longitude, options);
    } else if (
      unit.purpose === "repeater" ||
      unit.purpose === "repeater_long_range"
    ) {
      options.radius = 5;
      marker = createTriangle(unit.latitude, unit.longitude, options);
    }

    if (marker) {
      marker.bindTooltip("");
    }

    marker.unit_mac = unit_mac;
    marker_id = state.currentFarmMapLayer.addLayer(marker).getLayerId(marker);
  }

  return marker_id;
}

const setUnitMetadata = async ({ commit, state }, metadata) => {
  try {
    await api.setUnitMetadata(state.token, metadata).then(() => {
      commit("SET_UNIT_META_DATA", metadata);
    });
  } catch (err) {
    console.log(err);
    return;
  }

  const unit =
    state.farms[state.currentFarmId].endusers[metadata.enduser_id].units[
      metadata.unit_mac
    ];

  const farmUnit = state.currentFarmUnits[metadata.unit_mac];

  if (farmUnit.marker_id) {
    state.currentFarmMapLayer.removeLayer(farmUnit.marker_id);
  }

  const marker_id = addUnitMarker(state, metadata.unit_mac, metadata);

  unit.marker_id = marker_id;
  farmUnit.marker_id = marker_id;

  farmUnit.name = metadata.name;
};

const deleteUnit = async ({ commit, state }, metadata) => {
  try {
    await api.deleteUnit(state.token, metadata.unit_mac).then(() => {
      commit("DELETE_UNIT", metadata);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const swapUnits = async ({ state }, unit_macs) => {
  try {
    await api.swapUnits(state.token, unit_macs);
  } catch (err) {
    console.log(err);
    return;
  }
};

const newSchedule = async ({ commit, state }, name) => {
  try {
    const id = uuidv4();
    const schedule = {
      id: id,
      name: name,
      farm_id: state.currentFarmId,
    };

    return await api.saveSchedule(state.token, schedule).then(() => {
      commit("SAVE_SCHEDULE", schedule);

      return schedule.id;
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const getSchedules = async ({ commit, state }, farmId) => {
  try {
    // load available schedule id's and names
    await api.getSchedules(state.token, farmId).then((schedules) => {
      commit("SAVE_SCHEDULES", { farmId: farmId, schedules: schedules });
    });
  } catch (err) {
    console.log(err);
  }
};

const setActiveSchedule = async (
  { commit, state },
  { farmId, scheduleId, scheduleDirty }
) => {
  try {
    // load available schedule id's and names
    await api
      .setActiveSchedule(state.token, {
        id: farmId,
        schedule_id: scheduleId,
        schedule_dirty: scheduleDirty,
      })
      .then(() => {
        commit("SET_ACTIVE_SCHEDULE", {
          farmId: farmId,
          scheduleId: scheduleId,
          scheduleDirty: scheduleDirty,
        });
      });
  } catch (err) {
    console.log(err);
  }
};

const deployEnduserSchedule = async (
  { state, commit },
  { farmId, farmName, enduserId, scheduleId, enable }
) => {
  try {
    return await api
      .deployEnduserSchedule(state.token, {
        accountName: state.accountDetails.id,
        farmName: farmName,
        enduserId: enduserId,
        scheduleId: scheduleId,
        enable: enable,
      })
      .then(({ rimScheduleId, file }) => {
        commit("SET_ENDUSER_SCHEDULE_STATE", {
          farmId: farmId,
          enduserId: enduserId,
          rimScheduleId: rimScheduleId,
          enable: enable,
        });
        return file;
      });
  } catch (err) {
    console.log(err);
    return;
  }
};

const getScheduleGroups = async ({ commit, state }, scheduleId) => {
  try {
    await api.getScheduleGroups(state.token, scheduleId).then((groups) => {
      commit("SAVE_SCHEDULE_GROUPS", {
        scheduleId: scheduleId,
        groups: groups,
      });
    });

    await api
      .getScheduleGroupUnits(state.token, scheduleId)
      .then((groupUnits) => {
        commit("SAVE_SCHEDULE_GROUP_UNITS", {
          scheduleId: scheduleId,
          groupUnits: groupUnits,
        });

        // populate the currentFarmUnits object with the new schedule group
        // unit information
        for (const [mac, unit] of Object.entries(state.currentFarmUnits)) {
          const groupUnit = groupUnits[mac];
          unit.schedule_group = {
            id: groupUnit?.schedule_group_id ?? null,
            sort_index: groupUnit?.sort_index ?? 0,
          };
        }
      });
  } catch (err) {
    console.log(err);
    return;
  }
};

const getScheduleGroupUnits = async ({ commit, state }, scheduleId) => {
  try {
    await api
      .getScheduleGroupUnits(state.token, scheduleId)
      .then((groupUnits) => {
        commit("SAVE_SCHEDULE_GROUP_UNITS", {
          scheduleId: scheduleId,
          groupUnits: groupUnits,
        });
      });
  } catch (err) {
    console.log(err);
    return;
  }
};

const renameSchedule = async (context, { scheduleId, newName }) => {
  const { commit, state } = context;

  try {
    let currentSchedule = context.getters.currentFarmSchedules[scheduleId];
    let copy = { ...currentSchedule };
    copy.id = scheduleId;
    copy.name = newName;

    await api.saveSchedule(state.token, copy).then(() => {
      commit("SAVE_SCHEDULE", copy);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const deleteSchedule = async ({ commit, state }, id) => {
  try {
    await api.deleteSchedule(state.token, id).then(() => {
      commit("DELETE_SCHEDULE", id);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const newScheduleGroup = async ({ commit, state }, scheduleId) => {
  try {
    const id = uuidv4();
    const group = {
      id: id,
      name: "Untitled",
      start_time: [720],
      duration: [0],
      sequential: false,
      schedule_id: scheduleId,
      units: [],
    };

    return await api.saveScheduleGroup(state.token, group).then(() => {
      commit("SAVE_SCHEDULE_GROUP", group);

      return group.id;
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const saveScheduleGroup = async ({ commit, state }, group) => {
  try {
    await api.saveScheduleGroup(state.token, group).then(() => {
      commit("SAVE_SCHEDULE_GROUP", group);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const saveScheduleGroupUnits = async ({ state }, scheduleGroupUnits) => {
  try {
    await api.saveScheduleGroupUnits(state.token, scheduleGroupUnits);
  } catch (err) {
    console.log(err);
    return;
  }
};

const deleteScheduleGroupUnits = async ({ state }, scheduleGroupUnits) => {
  try {
    await api.deleteScheduleGroupUnits(state.token, scheduleGroupUnits);
  } catch (err) {
    console.log(err);
    return;
  }
};

const deleteScheduleGroup = async ({ commit, state }, group) => {
  try {
    await api.deleteScheduleGroup(state.token, group.id).then(() => {
      commit("DELETE_SCHEDULE_GROUP", group);
    });

    // synchronise currentFarmUnits with the changes
    for (const unitMac of group.units) {
      state.currentFarmUnits[unitMac].schedule_group = {
        id: null,
        sort_index: 0,
      };
    }
  } catch (err) {
    console.log(err);
    return;
  }
};

const newGroup = async ({ commit, state }, type) => {
  try {
    const id = uuidv4();
    const group = {
      id: id,
      group_type: type,
      name: "Untitled",
      coverage: 1.0,
      active: true,
    };

    return await api.saveGroup(state.token, group).then(() => {
      commit("SAVE_GROUP", group);

      return group.id;
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const saveGroup = async ({ commit, state }, group) => {
  try {
    await api.saveGroup(state.token, group).then(() => {
      commit("SAVE_GROUP", group);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const deleteGroup = async ({ commit, state }, group) => {
  try {
    await api.deleteGroup(state.token, group.id).then(() => {
      commit("DELETE_GROUP", group);
    });
  } catch (err) {
    console.log(err);
    return;
  }
};

const saveGroupUnits = async ({ state }, groupUnits) => {
  try {
    await api.saveGroupUnits(state.token, groupUnits);
  } catch (err) {
    console.log(err);
    return;
  }
};

async function initCurrentFarm(context) {
  const { state } = context;

  let units = {};

  state.currentFarmMapLayer.remove();
  state.currentFarmMapLayer.clearLayers();
  state.currentFarmMapLayer.off();

  for (const [enduserId, enduser] of Object.entries(
    context.getters.currentFarm.endusers
  )) {
    for (const [unit_mac, unit] of Object.entries(enduser.units)) {
      units = Object.assign(units, { [unit_mac]: unit.name });

      let marker_id = addUnitMarker(state, unit_mac, unit);

      unit.marker_id = marker_id;

      units = Object.assign(units, {
        [unit_mac]: {
          name: unit.name,
          purpose: unit.purpose,
          marker_id: marker_id,
          region_id: unit.group_region,
          terrain_id: unit.group_terrain,
          schedule_group: { id: unit.group_schedule, sort_index: 0 },
          sprinkler_active: false,
        },
      });
    }

    // get the current state of the active schedule for display in the home page
    if (context.getters.currentFarm.schedule_id && enduser.schedule_enabled) {
      await api
        .getEnduserScheduleState(
          state.token,
          enduserId,
          context.getters.currentFarm.schedule_id
        )
        .then((scheduleState) => {
          for (const mac of scheduleState) {
            units[mac].sprinkler_active = true;
          }
        });
    }
  }

  state.currentFarmUnits = units;
}

const refreshAllState = async (context) => {
  const { commit, state } = context;

  commit("SET_STATUS", "pending");

  // load the farms controlled by this account
  await api.getAllFarms(state.token).then((farms) => {
    commit("SET_FARMS_DATA", farms);
  });

  // load the enduser meta data for all endusers in this farm
  for (const key of Object.keys(state.farms)) {
    await api.getFarm(state.token, key).then((farm) => {
      commit("SET_FARM_DATA", { id: key, farm: farm });
    });

    // load available schedule id's and names
    await api.getSchedules(state.token, key).then((schedules) => {
      commit("SAVE_SCHEDULES", {
        farmId: key,
        schedules: schedules,
      });
    });

    // load the enduser stats for all endusers in this farm
    for (const enduser of Object.keys(context.getters.farms[key].endusers)) {
      await api.getEnduserStats(state.token, enduser).then((stats) => {
        commit("SET_ENDUSER_STATS", {
          farmId: key,
          enduserId: enduser,
          stats: stats,
        });
      });
    }
  }

  if (!state.currentFarmId) {
    // restore the current farm (if any)
    const currentFarmIdString = localStorage.getItem("currentFarmId");

    if (currentFarmIdString) {
      const curFarmId = JSON.parse(currentFarmIdString);
      commit("SET_CURRENT_FARM", curFarmId);
    }
  }

  commit("SET_STATUS", "success");
};

const refreshCurrentFarmState = async (context) => {
  const { commit, state } = context;

  if (!context.getters.currentFarm) {
    return;
  }

  commit("SET_STATUS", "pending");

  // load the enduser unit data for all endusers in this farm
  for (const key of Object.keys(context.getters.currentFarm.endusers)) {
    await api.getEnduser(state.token, key).then((units) => {
      commit("SET_ENDUSER_UNIT_DATA", {
        farm_id: state.currentFarmId,
        enduser_id: key,
        enduser_units: units,
      });
    });
  }

  // load available schedule id's and names
  await api.getSchedules(state.token, state.currentFarmId).then((schedules) => {
    commit("SAVE_SCHEDULES", {
      farmId: state.currentFarmId,
      schedules: schedules,
    });
  });

  // load the region groups
  await api
    .getGroups(state.token, state.currentFarmId, "region")
    .then((groups) => {
      commit("SAVE_GROUPS", {
        farmId: state.currentFarmId,
        groupType: "region",
        groups: groups,
      });
    });

  // load the terrain groups
  await api
    .getGroups(state.token, state.currentFarmId, "terrain")
    .then((groups) => {
      commit("SAVE_GROUPS", {
        farmId: state.currentFarmId,
        groupType: "terrain",
        groups: groups,
      });
    });

  await initCurrentFarm(context).then(() => commit("SET_STATUS", "success"));
};

export {
  login,
  logout,
  setUnitMetadata,
  deleteUnit,
  swapUnits,
  getSchedules,
  setActiveSchedule,
  deployEnduserSchedule,
  newSchedule,
  getScheduleGroups,
  getScheduleGroupUnits,
  renameSchedule,
  deleteSchedule,
  newScheduleGroup,
  saveScheduleGroup,
  saveScheduleGroupUnits,
  deleteScheduleGroupUnits,
  deleteScheduleGroup,
  newGroup,
  saveGroup,
  deleteGroup,
  saveGroupUnits,
  refreshAllState,
  refreshCurrentFarmState,
};
